import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import { TextStyleKit } from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import { ListItem } from "@tiptap/extension-list";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Superscript from "@tiptap/extension-superscript";
import Subscript from "@tiptap/extension-subscript";
import Youtube from "@tiptap/extension-youtube";
import HardBreak from "@tiptap/extension-hard-break";
import Document from "@tiptap/extension-document";
import { Placeholder } from "@tiptap/extensions";
import TextAlign from "@tiptap/extension-text-align";
import {
    Table,
    TableRow,
    TableCell,
    TableHeader,
} from "@tiptap/extension-table";
import Highlight from "@tiptap/extension-highlight";
import { Node } from "@tiptap/react";
const CustomDocument = Node.create({
    name: "doc",
    topNode: true,
    content:
        "(paragraph | heading  | blockquote | codeBlock | bulletList | orderedList | image | youtube | table)*",
});

const CustomParagraph = Node.create({
    name: "paragraph",
    content: "inline*",
    group: "block",
    parseHTML() {
        return [{ tag: "p" }];
    },
    renderHTML({ HTMLAttributes }) {
        return ["p", HTMLAttributes, 0];
    },
    addAttributes() {
        return {
            placeholder: {
                default: null,
                renderHTML: (attributes) => {
                    return {
                        "data-placeholder": attributes.placeholder,
                    };
                },
            },
        };
    },
    addCommands() {
        return {
            setParagraph:
                () =>
                ({ commands }) => {
                    return commands.setNode(this.name);
                },
        };
    },
});
// Make sure the export is properly defined as a named export
export const getBaseExtensions = (options = {}) => {
    const {
        includeTable = true,
        includeMedia = true,
        includeFormatting = true,
        includeAlignment = true,
        placeholder = {
            title: "Add title",
            heading: "Add heading...",
            paragraph: 'Start writing or type "/" for commands...',
        },
    } = options;

    let extensions = [
        CustomDocument,
        CustomParagraph,
        Color.configure({ types: [TextStyleKit.name, ListItem.name] }),
        TextStyleKit.configure({ types: [ListItem.name] }),
        StarterKit.configure({
            bulletList: {
                keepMarks: true,
                keepAttributes: false,
            },
            orderedList: {
                keepMarks: true,
                keepAttributes: false,
            },
            heading: {
                levels: [1, 2, 3, 4, 5, 6],
                HTMLAttributes: {
                    class: "heading-node",
                },
                keepOnlyInlineOnEnter: false, // This prevents auto-paragraph creation
            },
            history: true,
            document: false,
            paragraph: false,
            trailingNode: {
                notAfter: ["heading", "paragraph"],
            },
        }),
        Highlight.configure({
            multicolor: true, // This enables multiple highlight colors
            HTMLAttributes: {
                class: "custom-highlight",
            },
        }),
        HardBreak,
        Placeholder.configure({
            placeholder: ({ node }) => {
                if (node.type.name === "heading") return "Add heading...";
                if (node.type.name === "paragraph")
                    return 'Start writing or type "/" for commands...';
                return null;
            },
            showOnlyCurrent: false,
            emptyEditorClass: "is-editor-empty",
            emptyNodeClass: "is-empty",
        }),
    ];

    // Add formatting extensions
    if (includeFormatting) {
        extensions = [...extensions, Underline, Superscript, Subscript];
    }

    // Add alignment
    if (includeAlignment) {
        extensions.push(
            TextAlign.configure({
                types: ["heading", "paragraph", "tableCell", "tableHeader"],
                alignments: ["left", "center", "right", "justify"],
                defaultAlignment: "left",
            })
        );
    }

    // Add media extensions
    if (includeMedia) {
        extensions = [
            ...extensions,
            Image,
            Link.configure({
                openOnClick: true,
                linkOnPaste: true,
                HTMLAttributes: {
                    class: "editor-link",
                    rel: "noopener noreferrer",
                    target: "_blank",
                },
                protocols: ["http", "https", "mailto", "tel"],
                autolink: true,
            }),
            Youtube.configure({
                controls: false,
                nocookie: true,
            }),
        ];
    }

    // Add table extensions
    if (includeTable) {
        extensions = [
            ...extensions,
            Table.configure({
                resizable: true,
                lastColumnResizable: true,
                HTMLAttributes: {
                    class: "tiptap-table",
                },
                allowTableNodeSelection: true,
                defaultOptions: {
                    withColumnResizing: true,
                },
            }),
            TableCell,
            TableHeader,
            TableRow,
        ];
    }

    return extensions;
};

// Also export other necessary items
export const initialContent = `<h2>
Hi there,
</h2>
<p>
this is a <em>basic</em> example of <strong>Tiptap</strong>. Sure, there are all kind of basic text styles you’d probably expect from a text editor. But wait until you see the lists:
</p>
<ul>
<li>
  That’s a bullet list with one …
</li>
<li>
  … or two list items.
</li>
</ul>
<p>
Isn’t that great? And all of that is editable. But wait, there’s more. Let’s try a code block:
</p>
<pre><code class="language-css">body {
display: none;
}</code></pre>
<p>
I know, I know, this is impressive. It’s only the tip of the iceberg though. Give it a try and click a little bit around. Don’t forget to check the other examples too.
</p>
<blockquote>
Wow, that’s amazing. Good work, boy! 👏
<br />
— Mom
</blockquote>
`;

// Add a default export as a fallback in case it was imported that way
const editorConfig = {
    getBaseExtensions,
    initialContent,
};

export default editorConfig;
